/* eslint-disable no-param-reassign */
import type { Data } from '../../specProcessor';
import type { StandardChartLayerSpec } from '../../../../core/layer';
import type { Spec as ShadowBarLayerSpec } from '../../../../layer/preset/bar/shadowBar';
import * as utils from '../../../../util';
import {
  BaseSpec,
  ILayerTypeSpec,
  createDataTypeValidator,
  createEncodingValidator,
  createLayerProcessor
} from '../baseProcessor';

export type Spec = BaseSpec;

// 扩展ILayerTypeSpec以包含__isEncodingValid属性
interface ShadowBarLayerTypeWithFields extends ILayerTypeSpec {
  __isEncodingValid?: boolean;
}

type ShadowBarLayerType = StandardChartLayerSpec &
  ShadowBarLayerSpec &
  ShadowBarLayerTypeWithFields;

// 创建影子柱状图数据类型验证器
const validateShadowBarDataTypes = createDataTypeValidator('shadowBar', [
  { field: 'x', type: ['string', 'date'] },
  { field: 'y', type: 'number' },
  { field: 'z', type: 'string' }
]);

// 创建影子柱状图编码验证器
const validateShadowBarEncoding = createEncodingValidator([
  { field: 'x' },
  { field: 'y' },
  { field: 'z' }
]);

// 额外的检查函数
const additionalShadowBarChecks = (layer: ShadowBarLayerType, data: Data) => {
  const { encoding, type } = layer;
  const dataIndex = layer.dataIndex || 0;
  const values = data[dataIndex]?.values || [];

  if (!values.length || !encoding.z) {
    return;
  }

  // 检查Z轴分类是否至少有两个不同的值
  const zValues = new Set(values.map(item => item[encoding.z]));
  if (zValues.size < 2) {
    utils.logger.warn(
      'warning',
      `The "z" field of the "${type}" layer should have at least 2 different values`
    );
  }

  // 检查X轴数据是否有对应的Z值对
  if (layer.__isEncodingValid !== false) {
    // 检查每个X值是否有对应的两个Z值记录
    const xValueCounts = new Map<string | number, Set<string | number>>();
    values.forEach(item => {
      const xValue = item[encoding.x];
      const zValue = item[encoding.z];

      if (!xValueCounts.has(xValue)) {
        xValueCounts.set(xValue, new Set());
      }
      xValueCounts.get(xValue)?.add(zValue);
    });

    // 检查每个X值是否有多个Z值对应
    let hasInvalidPair = false;
    xValueCounts.forEach((zSet, xValue) => {
      if (zSet.size < 2) {
        hasInvalidPair = true;
        utils.logger.warn(
          'warning',
          `For "${type}" layer, each X value should have at least 2 corresponding records with different Z values. X value "${xValue}" only has ${zSet.size} Z value(s).`
        );
      }
    });

    if (hasInvalidPair) {
      utils.logger.warn(
        'warning',
        `The "${type}" layer data should have paired records for comparison.`
      );
    }
  }
};

// 创建并导出影子柱状图处理器
export const shadowBar = createLayerProcessor<ShadowBarLayerType>(
  'shadowBar',
  validateShadowBarEncoding,
  validateShadowBarDataTypes,
  additionalShadowBarChecks
);
